/* Shared draft transport for every personalized product template. */
(() => {
  if (globalThis.HeirloomPersonalizationServer) return;
  const root = document.querySelector('[data-personalization-endpoint]');
  const endpoint = root?.dataset.personalizationEndpoint || '';
  if (!endpoint) return;
  const pendingKey = '3dh-server-identity-pending-v1', receiptPrefix = '3dh-draft-identity-v2:', heartKey = '3dh-pending-server-draft-v1', uploadTokenKey = '3dh-temporary-upload-token-v1';
  const memory = new Map(), maxAge = 30 * 24 * 60 * 60 * 1000;
  const sessionPending = () => { try { const value = JSON.parse(sessionStorage.getItem(pendingKey) || '{}'); return value && typeof value === 'object' && !Array.isArray(value) ? value : {}; } catch { return {}; } };
  const validReceipt = value => value && /^3DH-[A-F0-9]{12}$/.test(value.requestId) && typeof value.editToken === 'string' && value.editToken.length > 0 && value.editToken.length <= 256;
  const forget = saved => {
    const key = receiptPrefix + saved.requestId;
    try { if (JSON.parse(localStorage.getItem(key) || 'null')?.editToken === saved.editToken) localStorage.removeItem(key); } catch { /* Storage may be disabled. */ }
    const drafts = sessionPending();
    if (drafts[saved.requestId]?.editToken === saved.editToken) {
      delete drafts[saved.requestId];
      try { if (Object.keys(drafts).length) sessionStorage.setItem(pendingKey, JSON.stringify(drafts)); else sessionStorage.removeItem(pendingKey); } catch { /* Memory recovery still works in this page. */ }
    }
    if (memory.get(saved.requestId)?.editToken === saved.editToken) memory.delete(saved.requestId);
  };
  const pending = () => {
    const drafts = new Map([...memory, ...Object.entries(sessionPending())]);
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(receiptPrefix)) continue;
        try { const value = JSON.parse(localStorage.getItem(key)); if (validReceipt(value)) drafts.set(value.requestId, value); } catch { /* Ignore unrelated or damaged storage. */ }
      }
    } catch { /* Use same-tab receipts when persistent storage is unavailable. */ }
    const active = [...drafts.values()].filter(value => {
      if (validReceipt(value) && Number.isFinite(value.expiresAt) && value.expiresAt > Date.now()) return true;
      if (validReceipt(value)) forget(value);
      return false;
    }).sort((a, b) => b.expiresAt - a.expiresAt);
    active.slice(100).forEach(forget);
    return active.slice(0, 100);
  };
  const remember = data => {
    if (!validReceipt(data)) return data;
    const suppliedExpiry = typeof data.expiresAt === 'number' ? data.expiresAt : Date.parse(data.expiresAt);
    const saved = { requestId: data.requestId, editToken: data.editToken, designId: data.customization?.designId || data.designId || '', expiresAt: Math.min(Number.isFinite(suppliedExpiry) ? suppliedExpiry : Date.now() + maxAge, Date.now() + maxAge) };
    if (saved.expiresAt <= Date.now()) { forget(saved); return data; }
    // Separate keys prevent one tab's successful claim from dropping another tab's new drafts.
    try {
      localStorage.setItem(receiptPrefix + saved.requestId, JSON.stringify(saved));
      const drafts = sessionPending(); delete drafts[saved.requestId];
      try { if (Object.keys(drafts).length) sessionStorage.setItem(pendingKey, JSON.stringify(drafts)); else sessionStorage.removeItem(pendingKey); } catch { /* The persistent copy is available. */ }
      memory.delete(saved.requestId);
    } catch {
      const drafts = sessionPending(); drafts[saved.requestId] = saved;
      try { sessionStorage.setItem(pendingKey, JSON.stringify(drafts)); } catch { memory.set(saved.requestId, saved); }
    }
    pending();
    return data;
  };
  Object.values(sessionPending()).forEach(remember);
  try {
    const saved = JSON.parse(sessionStorage.getItem(heartKey) || 'null');
    if (validReceipt(saved) && !saved.identityRecoverySaved) {
      remember(saved);
      sessionStorage.setItem(heartKey, JSON.stringify({ ...saved, identityRecoverySaved: true }));
    }
  } catch { /* Legacy cart recovery stays untouched if storage is unavailable. */ }

  const url = path => new URL(`${endpoint}${path}`, window.location.origin);
  const catalogImage = (...values) => {
    for (const value of values) {
      if (typeof value !== 'string' || !value.trim()) continue;
      try {
        const image = new URL(value.trim().replace(/^\/\//, 'https://'));
        if (image.protocol !== 'https:' || image.username || image.password || image.port) continue;
        if ((image.hostname === 'cdn.shopify.com' && image.pathname.startsWith('/s/files/'))
          || (image.hostname === '3dheirloom.com' && /^\/cdn\/shop\/(?:files|products)\/.+/.test(image.pathname))) return image.href;
      } catch { /* A missing or untrusted variant image can fall back to the product image. */ }
    }
    return '';
  };
  const productMetadata = (product = {}, variantId, quantity = 1) => {
    const variant = Array.isArray(product.variants) ? product.variants.find(item => String(item?.id) === String(variantId)) : null;
    const selectedOptions = Array.isArray(product.optionNames) ? product.optionNames.map((name, index) => ({ name, value: variant?.options?.[index] || '' })) : [];
    const option = name => selectedOptions.find(item => String(item.name).toLowerCase() === name)?.value || '';
    return {
      productTitle: typeof product.title === 'string' ? product.title : '',
      productUrl: typeof product.url === 'string' ? product.url : '',
      productImage: catalogImage(variant?.featured_image?.src, variant?.image?.src, variant?.image, product.image),
      shopifyVariantId: String(variantId || ''),
      sku: typeof variant?.sku === 'string' ? variant.sku : '',
      variantTitle: typeof variant?.title === 'string' ? variant.title : '',
      selectedOptions,
      size: option('size'), shape: option('shape'),
      dimensions: typeof product.dimensions === 'string' ? product.dimensions : '',
      quantity: Number.isSafeInteger(quantity) && quantity > 0 ? quantity : 1,
    };
  };
  const request = async (path, options = {}) => {
    const headers = new Headers(options.headers || {});
    headers.set('Accept', 'application/json');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), options.method === 'POST' ? 180000 : 15000);
    try {
      const response = await fetch(url(path), { ...options, credentials: 'same-origin', headers, signal: controller.signal });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw Object.assign(new Error(data.error || 'The personalization server could not save this design.'), { status: response.status, code: data.code });
      return data;
    } finally { clearTimeout(timer); }
  };
  let flowCapabilities = null;
  const capabilities = () => {
    if (flowCapabilities) return flowCapabilities;
    flowCapabilities = request('/v1/flow-capabilities').catch(error => { flowCapabilities = null; throw error; });
    return flowCapabilities;
  };
  const ensurePurchaseFlow = () => capabilities().then(value => {
      if (value.optionalPreviewRequests !== true) throw new Error('Personalization saving is temporarily unavailable. Your details have not been submitted. Please try again later.');
      return true;
    }).catch(() => {
      throw new Error('Personalization saving is temporarily unavailable. Your details have not been submitted. Please try again later.');
    });
  const ensureEagerUploads = () => capabilities().then(value => value.eagerUploads === true);
  const ensureCheckoutAssetRefs = () => capabilities().then(value => value.checkoutAssetRefs === true && value.lineItemAssetRefs === true);
  const digest = async file => {
    if (!(file instanceof Blob) || !globalThis.crypto?.subtle) throw new Error('Photo uploads require a secure connection. Reload this page using HTTPS.');
    const value = await globalThis.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return [...new Uint8Array(value)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  };
  const storedUploadToken = () => { try { return sessionStorage.getItem(uploadTokenKey) || ''; } catch { return ''; } };
  const rememberUploadToken = value => { if (!value) return; try { sessionStorage.setItem(uploadTokenKey, value); } catch { /* The current tab can still continue without persistence. */ } };
  let pageUploadToken = '';
  const uploadVersions = new Map(), activeUploads = new Set();
  let uploadGeneration = 0;
  globalThis.addEventListener('pagehide', () => { uploadGeneration++; activeUploads.forEach(xhr => xhr.abort()); });
  const uploadAsset = async ({ productId, variantId, purpose = 'source', slot = 'photo-1', version = 1, file, sha256 = '', signal, onProgress = () => {} }) => {
    const generation = uploadGeneration;
    const aborted = () => Object.assign(new Error('Photo upload canceled. Your details are still here.'), { name: 'AbortError' });
    if (signal?.aborted || generation !== uploadGeneration) throw aborted();
    onProgress({ phase: 'preparing', percent: null });
    if (!productId || !variantId || !file) throw new Error('Choose a photo before uploading.');
    // A reload starts a new upload scope; restored assets retain their original credential.
    if (!pageUploadToken) pageUploadToken = globalThis.crypto?.randomUUID
      ? `${globalThis.crypto.randomUUID().replaceAll('-', '')}${globalThis.crypto.randomUUID().replaceAll('-', '')}`
      : Array.from(globalThis.crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
    const token = pageUploadToken, scope = JSON.stringify([productId, String(variantId), purpose, slot]);
    version = Math.max(version, (uploadVersions.get(scope) || 0) + 1);
    uploadVersions.set(scope, version);
    rememberUploadToken(token);
    const body = new FormData();
    body.set('productId', productId); body.set('variantId', String(variantId)); body.set('purpose', purpose); body.set('slot', slot); body.set('version', String(version));
    body.set('uploadId', globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    body.set('sha256', sha256 || await digest(file));
    body.set('asset', file, file.name || 'personalization-reference');
    if (signal?.aborted || generation !== uploadGeneration) throw aborted();
    const data = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const cancel = () => xhr.abort();
      const finish = (error, value) => {
        activeUploads.delete(xhr);
        signal?.removeEventListener('abort', cancel);
        xhr.onload = xhr.onerror = xhr.onabort = xhr.ontimeout = null;
        xhr.upload.onprogress = xhr.upload.onload = null;
        if (error) reject(error); else resolve(value);
      };
      xhr.open('POST', url('/v1/assets').href);
      xhr.timeout = 180000;
      xhr.setRequestHeader('Accept', 'application/json');
      if (token) xhr.setRequestHeader('x-upload-token', token);
      xhr.upload.onprogress = event => onProgress({ phase: 'transferring', percent: event.lengthComputable && event.total > 0 ? Math.min(100, Math.floor(event.loaded / event.total * 100)) : null });
      xhr.upload.onload = () => onProgress({ phase: 'saving', percent: null });
      xhr.onload = () => {
        let value; try { value = JSON.parse(xhr.responseText); } catch { value = {}; }
        if (xhr.status < 200 || xhr.status >= 300) return finish(Object.assign(new Error(value.error || 'The photo could not be saved. Please retry.'), { status: xhr.status, code: value.code }));
        if (!value.assetId || !value.uploadToken || value.productId !== productId || String(value.variantId) !== String(variantId) || value.purpose !== purpose || value.slot !== slot || value.version !== version || value.sha256 !== body.get('sha256') || !(Date.parse(value.expiresAt) > Date.now())) return finish(new Error('The saved photo could not be confirmed. Please retry.'));
        finish(null, value);
      };
      xhr.onerror = () => finish(new Error('The connection failed. Your details are still here. Please retry.'));
      xhr.ontimeout = () => finish(new Error('Photo saving timed out. Your details are still here. Please retry.'));
      xhr.onabort = () => finish(aborted());
      activeUploads.add(xhr);
      signal?.addEventListener('abort', cancel, { once: true });
      onProgress({ phase: 'transferring', percent: null });
      try { xhr.send(body); } catch (error) { finish(error); }
    });
    if (signal?.aborted || generation !== uploadGeneration) throw aborted();
    onProgress({ phase: 'success', percent: 100 });
    rememberUploadToken(data.uploadToken);
    return data;
  };
  const draft = async ({ productId, variantId, sku = '', product = null, customization = {}, file = null, preview = null, assetRefs = null, uploadToken = '', requestId = '', editToken = '', purchaseIntent = '' }) => {
    if (!productId || !variantId) throw new Error('The product configuration could not be verified.');
    if (purchaseIntent === 'purchase') await ensurePurchaseFlow();
    const body = new FormData();
    const value = { ...customization, ...(product ? productMetadata(product, variantId, customization.quantity) : {}), productId, variantId, sku };
    value.productImage = catalogImage(value.productImage);
    if ('baseImage' in value) value.baseImage = catalogImage(value.baseImage);
    body.set('productId', productId);
    body.set('variantId', String(variantId));
    body.set('sku', sku);
    body.set('customization', JSON.stringify(value));
    body.set('consentProduction', 'true');
    if (purchaseIntent) body.set('purchaseIntent', purchaseIntent);
    if (root.dataset.customerEmail) body.set('customer_email', root.dataset.customerEmail);
    if (requestId) body.set('requestId', requestId);
    if (Array.isArray(assetRefs) && assetRefs.length) body.set('assetRefs', JSON.stringify(assetRefs));
    else {
      if (file instanceof Blob) body.set('file', file, file.name || 'personalization-reference');
      if (preview instanceof Blob) body.set('preview', preview, preview.name || 'personalization-preview.jpg');
    }
    const headers = {};
    if (editToken) headers['x-draft-token'] = editToken;
    const token = uploadToken || assetRefs?.[0]?.uploadToken || storedUploadToken();
    if (assetRefs?.some(ref => ref.uploadToken && ref.uploadToken !== token)) throw new Error('These photos belong to different upload sessions. Please upload the photos together again.');
    if (token) headers['x-upload-token'] = token;
    const data = await request('/v1/drafts', { method: 'POST', body, headers });
    return remember({ ...data, editToken: data.editToken || editToken });
  };
  const permanentFailure = error => error.status === 404 || error.status === 410 || error.code === 'draft_customer_conflict';
  const claims = new Map();
  const claim = ({ requestId, editToken, email = '', customerId = '' }) => {
    if (!requestId || !editToken) return false;
    const key = requestId + ':' + editToken;
    if (claims.has(key)) return claims.get(key);
    const running = (async () => {
      try {
        await request(`/v1/drafts/${encodeURIComponent(requestId)}/identity`, {
          method: 'PATCH', body: JSON.stringify({ customer_email: email, ...(customerId ? { expected_customer_id: customerId } : {}) }),
          headers: { 'content-type': 'application/json', 'x-draft-token': editToken },
        });
        forget({ requestId, editToken });
        return true;
      } catch (error) { if (permanentFailure(error)) forget({ requestId, editToken }); throw error; }
      finally { claims.delete(key); }
    })();
    claims.set(key, running);
    return running;
  };
  const customer = node => {
    if (node?.dataset.customerLoggedIn === 'false' || !node?.dataset.customerEmail) throw new Error('Sign in to reconnect your saved designs.');
    return { email: node.dataset.customerEmail, customerId: /^[0-9]+$/.test(node.dataset.customerId || '') ? node.dataset.customerId : '' };
  };
  let identityRead = null;
  const readIdentity = node => {
    if (node?.dataset.customerLoggedIn === 'true' && /^[0-9]+$/.test(node.dataset.customerId || '')) return Promise.resolve(customer(node));
    if (identityRead) return identityRead;
    identityRead = (async () => {
      const controller = new AbortController(), timer = setTimeout(() => controller.abort(), 15000);
      try {
        const page = await fetch(window.location.href, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
        if (!page.ok) throw new Error('Could not read the signed-in customer.');
        const html = new DOMParser().parseFromString(await page.text(), 'text/html');
        return customer(html.querySelector('[data-personalization-identity]') || html.querySelector('[data-customer-email]'));
      } finally { clearTimeout(timer); identityRead = null; }
    })();
    return identityRead;
  };
  const claimCurrent = async saved => claim({ ...saved, ...await readIdentity() });
  let claiming = null;
  const claimPending = node => {
    if (claiming) return claiming;
    const saved = pending();
    if (!saved.length) return Promise.resolve();
    claiming = (async () => {
      const identity = await readIdentity(node);
      let changed = false;
      for (const draft of saved) {
        try { changed = await claim({ ...draft, ...identity }) || changed; }
        catch (error) { if (!permanentFailure(error)) throw error; }
      }
      if (changed) void globalThis.HeirloomCartState?.sync({ resume: true, force: true });
    })().finally(() => { claiming = null; });
    return claiming;
  };
  let lastResume = 0;
  const resume = event => {
    if (document.visibilityState === 'hidden' || !pending().length) return;
    if (!event?.persisted && Date.now() - lastResume < 1500) return;
    lastResume = Date.now();
    void claimPending().catch(() => { /* Keep receipts for the next login or explicit cart refresh. */ });
  };
  globalThis.HeirloomPersonalizationServer = { request, draft, uploadAsset, ensurePurchaseFlow, ensureEagerUploads, ensureCheckoutAssetRefs, claim, claimCurrent, remember, claimPending, pending, productMetadata, catalogImage };
  globalThis.addEventListener?.('focus', resume);
  globalThis.addEventListener?.('pageshow', resume);
  if (root.dataset.customerLoggedIn === 'true') {
    lastResume = Date.now();
    void claimPending(root).catch(() => { /* A cart refresh can retry visibly after a temporary failure. */ });
  }
})();
